import { useParams, Link, useNavigate } from 'react-router-dom';
import { useContent } from '../hooks/useContent';
import { useToast } from '../hooks/useToast';
import { getContentId } from '../types';
import DraggableList from '../components/DraggableList';
import '../styles/editor.css';

export default function CategoryList() {
  const { siteId } = useParams<{ siteId: string }>();
  const { items, loading, error, remove, reorder } = useContent(siteId, 'category');
  const { addToast } = useToast();
  const navigate = useNavigate();

  const handleDelete = async (contentKey: string, name: string) => {
    if (!confirm(`Delete category "${name}"?`)) return;
    try {
      const id = getContentId(contentKey);
      await remove(id);
      addToast('success', 'Category deleted');
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  const handleReorder = async (fromIndex: number, toIndex: number) => {
    const newItems = [...items];
    const [moved] = newItems.splice(fromIndex, 1);
    newItems.splice(toIndex, 0, moved);
    const ordered = newItems.map((item, i) => ({
      id: getContentId(item.contentKey),
      sortOrder: i,
    }));
    try {
      await reorder(ordered);
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'Failed to reorder');
    }
  };

  if (loading) {
    return (
      <div className="page">
        <div className="page-header"><h1>Categories</h1></div>
        <div className="loading-state"><div className="spinner" /></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page">
        <div className="page-header"><h1>Categories</h1></div>
        <div className="error-state"><p>{error}</p></div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1>Categories</h1>
        <Link to={`/sites/${siteId}/content/categories/new`} className="btn btn-primary">
          + New Category
        </Link>
      </div>

      {items.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon"><svg width={48} height={48} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" style={{ color: '#ccc' }}><path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/></svg></div>
          <h2>No categories yet</h2>
          <p>Create categories to organize your products.</p>
          <Link to={`/sites/${siteId}/content/categories/new`} className="btn btn-primary">
            Create your first category
          </Link>
        </div>
      ) : (
        <DraggableList
          items={items.map((item) => {
            const data = item.data as Record<string, string>;
            const contentId = getContentId(item.contentKey);
            return {
              key: item.contentKey,
              element: (
                <>
                  <div
                    className="list-item-content"
                    onClick={() =>
                      navigate(`/sites/${siteId}/content/categories/${contentId}`)
                    }
                  >
                    <h3 className="list-item-title">{data.name || 'Untitled'}</h3>
                    <div className="list-item-meta">
                      {data.description && (
                        <span className="list-item-price">{data.description}</span>
                      )}
                      {data.sortOrder && (
                        <span className="tag">Order: {data.sortOrder}</span>
                      )}
                    </div>
                  </div>
                  <div className="list-item-actions">
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => handleDelete(item.contentKey, data.name || 'this category')}
                    >
                      Delete
                    </button>
                  </div>
                </>
              ),
            };
          })}
          onReorder={handleReorder}
        />
      )}
    </div>
  );
}
